import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { formatApiError } from "@/lib/AuthContext";
import { Card, Btn, Pill, Modal, inputCls } from "@/dashboard/ui";
import { Sparkles, Check, X, Pencil, History, Send } from "lucide-react";

const CHANNELS = ["Instagram", "Facebook", "LinkedIn", "X"];
const FREQS = [
  { id: "light", label: "Light — 8 posts", hint: "about 2 a week" },
  { id: "standard", label: "Standard — 15 posts", hint: "recommended" },
  { id: "active", label: "Daily — 30 posts", hint: "one per day" },
];
const FORMATS = ["photo", "video", "carousel", "story"];
const FORMAT_LABELS = { photo: "Photo / image", video: "Video / Reel", carousel: "Carousel", story: "Story" };

const PHOTO_FIELDS = [["subject", "What to photograph"], ["setting", "Location / background"], ["composition", "Composition & camera angle"], ["lighting", "Lighting"], ["props", "Props"], ["overlay", "Text overlay"], ["communicates", "What the image should communicate"]];
const VIDEO_FIELDS = [["duration", "Suggested duration"], ["hook", "Opening hook"], ["script", "Spoken script / talking points"], ["onscreen_text", "On-screen text"], ["closing", "Closing scene"], ["cta", "Video call to action"]];
const STORY_FIELDS = [["overlay_text", "Overlay text"], ["interaction", "Poll / question / interaction"], ["cta", "Story call to action"]];

function newKey() {
  return `plan-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export function EditDraftModal({ draft, onClose, onSaved }) {
  const fmt0 = draft.format || "photo";
  const [form, setForm] = useState({
    topic: draft.title, caption: draft.content || "",
    hashtags: (draft.hashtags || []).join(" "), keywords: (draft.keywords || []).join(", "),
    cta: draft.cta || "", objective: draft.objective || "",
    production_notes: draft.production_notes || "", location_tag: draft.location_tag || "",
    alt_text: draft.alt_text || "",
    planned_date: (draft.scheduled_at || "").slice(0, 10),
    platforms: draft.platforms || [], format: fmt0,
    photo: draft.photo || {}, video: draft.video || {},
    carousel: draft.carousel || {}, story: draft.story || {},
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setBrief = (k, sub, v) => setForm((f) => ({ ...f, [k]: { ...f[k], [sub]: v } }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        topic: form.topic, caption: form.caption,
        hashtags: form.hashtags.split(/[,\s]+/).filter(Boolean),
        keywords: form.keywords.split(",").map((s) => s.trim()).filter(Boolean),
        cta: form.cta, objective: form.objective, production_notes: form.production_notes,
        planned_date: form.planned_date, platforms: form.platforms, format: form.format,
        photo: form.format === "photo" ? form.photo : null,
        video: form.format === "video" ? form.video : null,
        carousel: form.format === "carousel" ? {
          slide_count: form.carousel.slide_count || "",
          final_cta: form.carousel.final_cta || "",
          slides: (form.carousel.slidesText ?? (form.carousel.slides || []).map((s) => `${s.headline} — ${s.point} — ${s.visual}`).join("\n"))
            .split("\n").filter((l) => l.trim())
            .map((l) => { const [headline = "", point = "", visual = ""] = l.split("—").map((x) => x.trim()); return { headline, point, visual }; }),
        } : null,
        story: form.format === "story" ? {
          ...form.story,
          frames: form.story.framesText ?? form.story.frames ?? "",
        } : null,
        version: draft.version,
      };
      const { data } = await api.put(`/marketing/content-drafts/${draft.id}`, payload);
      toast.success("Draft saved");
      onSaved(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const channel = form.platforms[0];
  const allowedFormats = FORMATS.filter((f) => !(channel === "X" && ["carousel", "story"].includes(f)) && !(channel === "LinkedIn" && f === "story"));

  return (
    <Modal title="Post creative brief" onClose={onClose} testid="content-draft-edit-modal">
      <div className="max-h-[70vh] space-y-3 overflow-y-auto pr-1">
        <div className="grid grid-cols-2 gap-3">
          <label className="col-span-2 block">
            <span className="mb-1 block text-xs font-semibold text-neutral-600">Topic</span>
            <input className={inputCls} value={form.topic} data-testid="edit-draft-topic" onChange={(e) => set("topic", e.target.value)} />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-semibold text-neutral-600">Planned date</span>
            <input type="date" className={inputCls} value={form.planned_date} data-testid="edit-draft-date" onChange={(e) => set("planned_date", e.target.value)} />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-semibold text-neutral-600">Marketing objective</span>
            <input className={inputCls} value={form.objective} data-testid="edit-draft-objective" onChange={(e) => set("objective", e.target.value)} />
          </label>
        </div>
        <div>
          <span className="mb-1 block text-xs font-semibold text-neutral-600">Platform</span>
          <div className="flex flex-wrap gap-1.5">
            {CHANNELS.map((c) => (
              <button key={c} type="button" data-testid={`edit-draft-channel-${c.toLowerCase()}`}
                onClick={() => set("platforms", form.platforms.includes(c) ? form.platforms.filter((x) => x !== c) : [c])}
                className={`rounded-full border px-3 py-1 text-xs font-medium ${form.platforms.includes(c) ? "border-[#111111] bg-[#111111] text-white" : "border-neutral-300 text-neutral-600"}`}>
                {c}
              </button>
            ))}
          </div>
        </div>
        <div>
          <span className="mb-1 block text-xs font-semibold text-neutral-600">Content format</span>
          <div className="flex flex-wrap gap-1.5" data-testid="edit-draft-formats">
            {allowedFormats.map((f) => (
              <button key={f} type="button" data-testid={`edit-draft-format-${f}`} onClick={() => set("format", f)}
                className={`rounded-full border px-3 py-1 text-xs font-medium ${form.format === f ? "border-[#111111] bg-[#111111] text-white" : "border-neutral-300 text-neutral-600"}`}>
                {FORMAT_LABELS[f]}
              </button>
            ))}
          </div>
        </div>

        {form.format === "photo" && (
          <div className="space-y-2 rounded-xl border border-neutral-200 p-3" data-testid="edit-draft-photo-fields">
            <p className="text-xs font-bold text-neutral-700">Photo instructions</p>
            {PHOTO_FIELDS.map(([k, label]) => (
              <label key={k} className="block">
                <span className="mb-0.5 block text-[11px] text-neutral-500">{label}</span>
                <input className={inputCls} value={form.photo[k] || ""} data-testid={`edit-photo-${k}`} onChange={(e) => setBrief("photo", k, e.target.value)} />
              </label>
            ))}
          </div>
        )}
        {form.format === "video" && (
          <div className="space-y-2 rounded-xl border border-neutral-200 p-3" data-testid="edit-draft-video-fields">
            <p className="text-xs font-bold text-neutral-700">Video / Reel instructions</p>
            {VIDEO_FIELDS.map(([k, label]) => (
              <label key={k} className="block">
                <span className="mb-0.5 block text-[11px] text-neutral-500">{label}</span>
                <input className={inputCls} value={form.video[k] || ""} data-testid={`edit-video-${k}`} onChange={(e) => setBrief("video", k, e.target.value)} />
              </label>
            ))}
            <label className="block">
              <span className="mb-0.5 block text-[11px] text-neutral-500">Shot list (one shot per line)</span>
              <textarea rows={3} className={inputCls} value={form.video.shots || ""} data-testid="edit-video-shots" onChange={(e) => setBrief("video", "shots", e.target.value)} />
            </label>
          </div>
        )}
        {form.format === "carousel" && (
          <div className="space-y-2 rounded-xl border border-neutral-200 p-3" data-testid="edit-draft-carousel-fields">
            <p className="text-xs font-bold text-neutral-700">Carousel instructions</p>
            <label className="block">
              <span className="mb-0.5 block text-[11px] text-neutral-500">Suggested slide count</span>
              <input className={inputCls} value={form.carousel.slide_count || ""} data-testid="edit-carousel-count" onChange={(e) => setBrief("carousel", "slide_count", e.target.value)} />
            </label>
            <label className="block">
              <span className="mb-0.5 block text-[11px] text-neutral-500">Slides — one per line: headline — main point — suggested visual</span>
              <textarea rows={4} className={inputCls} data-testid="edit-carousel-slides"
                defaultValue={(form.carousel.slides || []).map((s) => `${s.headline} — ${s.point} — ${s.visual}`).join("\n")}
                onChange={(e) => setBrief("carousel", "slidesText", e.target.value)} />
            </label>
            <label className="block">
              <span className="mb-0.5 block text-[11px] text-neutral-500">Final CTA slide</span>
              <input className={inputCls} value={form.carousel.final_cta || ""} data-testid="edit-carousel-cta" onChange={(e) => setBrief("carousel", "final_cta", e.target.value)} />
            </label>
          </div>
        )}
        {form.format === "story" && (
          <div className="space-y-2 rounded-xl border border-neutral-200 p-3" data-testid="edit-draft-story-fields">
            <p className="text-xs font-bold text-neutral-700">Story instructions</p>
            <label className="block">
              <span className="mb-0.5 block text-[11px] text-neutral-500">Frame sequence (one per line)</span>
              <textarea rows={3} className={inputCls} value={form.story.framesText ?? form.story.frames ?? ""} data-testid="edit-story-frames" onChange={(e) => setBrief("story", "framesText", e.target.value)} />
            </label>
            {STORY_FIELDS.map(([k, label]) => (
              <label key={k} className="block">
                <span className="mb-0.5 block text-[11px] text-neutral-500">{label}</span>
                <input className={inputCls} value={form.story[k] || ""} data-testid={`edit-story-${k}`} onChange={(e) => setBrief("story", k, e.target.value)} />
              </label>
            ))}
          </div>
        )}

        <label className="block">
          <span className="mb-1 block text-xs font-semibold text-neutral-600">Caption</span>
          <textarea rows={4} className={inputCls} value={form.caption} data-testid="edit-draft-caption" onChange={(e) => set("caption", e.target.value)} />
        </label>
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="mb-1 block text-xs font-semibold text-neutral-600">Hashtags</span>
            <input className={inputCls} value={form.hashtags} data-testid="edit-draft-hashtags" onChange={(e) => set("hashtags", e.target.value)} />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-semibold text-neutral-600">Keywords (comma separated)</span>
            <input className={inputCls} value={form.keywords} data-testid="edit-draft-keywords" onChange={(e) => set("keywords", e.target.value)} />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-semibold text-neutral-600">Call to action</span>
            <input className={inputCls} value={form.cta} data-testid="edit-draft-cta" onChange={(e) => set("cta", e.target.value)} />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-semibold text-neutral-600">Location tag (optional)</span>
            <input className={inputCls} value={form.location_tag} data-testid="edit-draft-location" onChange={(e) => set("location_tag", e.target.value)} />
          </label>
        </div>
        <label className="block">
          <span className="mb-1 block text-xs font-semibold text-neutral-600">Accessibility alt text</span>
          <input className={inputCls} value={form.alt_text} data-testid="edit-draft-alt" onChange={(e) => set("alt_text", e.target.value)} />
        </label>
        <label className="block">
          <span className="mb-1 block text-xs font-semibold text-neutral-600">Production notes</span>
          <textarea rows={2} className={inputCls} value={form.production_notes} data-testid="edit-draft-notes" onChange={(e) => set("production_notes", e.target.value)} />
        </label>
        <div className="flex justify-end gap-2 pt-1">
          <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
          <Btn data-testid="edit-draft-save" disabled={saving} onClick={save}>{saving ? "Saving…" : "Save draft"}</Btn>
        </div>
      </div>
    </Modal>
  );
}

export default function ContentPlanPanel() {
  const [strategy, setStrategy] = useState(undefined);
  const [plans, setPlans] = useState([]);
  const [current, setCurrent] = useState(null);
  const [startDate, setStartDate] = useState(new Date().toISOString().slice(0, 10));
  const [channels, setChannels] = useState(["Instagram", "Facebook"]);
  const [frequency, setFrequency] = useState("standard");
  const [usage, setUsage] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [overlap, setOverlap] = useState(null);
  const [selected, setSelected] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showPlans, setShowPlans] = useState(false);
  const [social, setSocial] = useState(null);
  const [publishing, setPublishing] = useState(null);
  const [publishBusy, setPublishBusy] = useState(false);
  const [, setParams] = useSearchParams();
  const idemKey = useRef(newKey());

  const load = async () => {
    try {
      const [{ data: s }, { data: p }, { data: u }] = await Promise.all([
        api.get("/marketing/strategies"), api.get("/marketing/content-plans"), api.get("/usage"),
      ]);
      setStrategy(s.strategies.find((x) => x.status === "approved") || null);
      setPlans(p.plans.filter((x) => x.status !== "generating"));
      setUsage(u.usage?.marketing_generate || null);
      const latest = p.plans.find((x) => x.status !== "generating");
      if (latest && !current) openPlan(latest.id);
    } catch (e) {
      toast.error(formatApiError(e));
    }
    api.get("/social/status").then(({ data }) => setSocial(data)).catch(() => setSocial(null));
  };

  const fbConn = (social?.connections || []).find((c) => c.platform === "facebook");

  const publish = async () => {
    setPublishBusy(true);
    try {
      await api.post(`/marketing/content-drafts/${publishing.id}/publish`);
      toast.success(`Published to ${fbConn?.display_name || "your Facebook Page"}`);
      setPublishing(null);
      await openPlan(current.plan.id);
    } catch (e) {
      toast.error(formatApiError(e));
      setPublishing(null);
    } finally {
      setPublishBusy(false);
    }
  };

  const openPlan = async (id) => {
    try {
      const { data } = await api.get(`/marketing/content-plans/${id}`);
      setCurrent(data);
      setSelected([]);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const generate = async (confirmOverlap = false) => {
    setGenerating(true);
    setOverlap(null);
    try {
      const { data } = await api.post("/marketing/content-plans/generate", {
        start_date: startDate, channels, frequency, confirm_overlap: confirmOverlap,
      }, { headers: { "Idempotency-Key": idemKey.current } });
      if (data.deduplicated) {
        toast.info("That plan was already generated — opened it for you.");
      } else {
        toast.success(`${data.post_count} posts planned — review them below`);
      }
      idemKey.current = newKey(); // next generation is a fresh request
      await load();
      await openPlan(data.plan.id);
    } catch (e) {
      if (e.response?.status === 409 && e.response?.data?.detail?.error === "overlap") {
        setOverlap(e.response.data.detail);
      } else {
        toast.error(formatApiError(e));
      }
    } finally {
      setGenerating(false);
    }
  };

  const review = async (ids, action) => {
    try {
      await api.post("/marketing/content-drafts/review", { ids, action });
      toast.success(action === "approve" ? `${ids.length} post(s) approved — find them on the Content Calendar tab` : "Draft(s) rejected");
      await openPlan(current.plan.id);
      setSelected([]);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (strategy === undefined) return null;

  return (
    <div className="space-y-5" data-testid="content-plan-panel">
      <Card className="p-6" data-testid="content-plan-generate-card">
        <p className="text-xs font-bold uppercase tracking-wider text-neutral-500">AI 30-day content plan</p>
        {!strategy ? (
          <div className="mt-3" data-testid="content-plan-no-strategy">
            <p className="text-sm text-neutral-600">Approve an AI Marketing Strategy first to generate your content calendar.</p>
            <Btn className="mt-3" variant="ghost" data-testid="content-plan-goto-strategy" onClick={() => setParams({ tab: "strategy" })}>
              Go to AI Strategy
            </Btn>
          </div>
        ) : (
          <>
            <p className="mt-2 text-xs text-neutral-500" data-testid="content-plan-strategy-name">
              Using your approved strategy from {String(strategy.approved_at || strategy.created_at).slice(0, 10)}
              {usage && <> · AI marketing allowance left this month: <span className="font-semibold text-neutral-700" data-testid="content-plan-usage">{usage.remaining}</span></>}
            </p>
            <div className="mt-4 flex flex-wrap items-end gap-4">
              <label>
                <span className="mb-1 block text-xs font-semibold text-neutral-600">Start date</span>
                <input type="date" className={inputCls} value={startDate} data-testid="content-plan-start-date" onChange={(e) => setStartDate(e.target.value)} />
              </label>
              <div>
                <span className="mb-1 block text-xs font-semibold text-neutral-600">Channels <span className="font-normal text-neutral-400">(planning only — nothing is published)</span></span>
                <div className="flex flex-wrap gap-1.5">
                  {CHANNELS.map((c) => (
                    <button key={c} type="button" data-testid={`content-plan-channel-${c.toLowerCase()}`}
                      onClick={() => setChannels(channels.includes(c) ? channels.filter((x) => x !== c) : [...channels, c])}
                      className={`rounded-full border px-3 py-1 text-xs font-medium ${channels.includes(c) ? "border-[#111111] bg-[#111111] text-white" : "border-neutral-300 text-neutral-600"}`}>
                      {c}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <span className="mb-1 block text-xs font-semibold text-neutral-600">Plan size</span>
                <div className="flex flex-wrap gap-1.5" data-testid="content-plan-frequency">
                  {FREQS.map((f) => (
                    <button key={f.id} type="button" data-testid={`content-plan-freq-${f.id}`} onClick={() => setFrequency(f.id)}
                      className={`rounded-full border px-3 py-1 text-xs font-medium ${frequency === f.id ? "border-[#111111] bg-[#111111] text-white" : "border-neutral-300 text-neutral-600"}`}>
                      {f.label} <span className="opacity-60">· {f.hint}</span>
                    </button>
                  ))}
                </div>
              </div>
              <Btn data-testid="content-plan-generate-button" disabled={generating || channels.length === 0} onClick={() => generate(false)}>
                <Sparkles size={14} className="mr-1.5 inline" />
                {generating ? "Generating plan…" : "Generate 30-Day Plan"}
              </Btn>
            </div>
            {overlap && (
              <div className="mt-4 rounded-xl border border-amber-300 bg-amber-50 p-4" data-testid="content-plan-overlap-warning">
                <p className="text-sm font-semibold text-amber-900">{overlap.message}</p>
                <p className="mt-1 text-xs text-amber-800">
                  Existing plan{overlap.plans.length > 1 ? "s" : ""}: {overlap.plans.map((p) => `${p.start_date} → ${p.end_date} (${p.status})`).join(", ")}.
                  Nothing was generated and no AI allowance was used.
                </p>
                <div className="mt-3 flex gap-2">
                  <Btn data-testid="content-plan-overlap-confirm" disabled={generating} onClick={() => generate(true)}>Create a separate plan anyway</Btn>
                  <Btn variant="ghost" onClick={() => setOverlap(null)}>Cancel</Btn>
                </div>
              </div>
            )}
          </>
        )}
      </Card>

      {current && (
        <Card className="p-6" data-testid="content-plan-review-card">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <p className="text-xs font-bold uppercase tracking-wider text-neutral-500">
              Review creative briefs — {current.plan.start_date} → {current.plan.end_date} ({current.drafts.length} posts)
            </p>
            <Btn data-testid="content-plan-bulk-approve" disabled={selected.length === 0} onClick={() => review(selected, "approve")}>
              <Check size={14} className="mr-1 inline" /> Approve selected ({selected.length})
            </Btn>
          </div>
          <div className="space-y-2">
            {current.drafts.map((d) => (
              <div key={d.id} className="flex flex-wrap items-center gap-3 rounded-xl border border-neutral-200 px-4 py-3" data-testid={`content-draft-row-${d.id}`}>
                <input type="checkbox" className="h-4 w-4 accent-neutral-900" data-testid={`content-draft-select-${d.id}`}
                  checked={selected.includes(d.id)}
                  onChange={(e) => setSelected(e.target.checked ? [...selected, d.id] : selected.filter((x) => x !== d.id))} />
                <span className="w-24 text-xs font-semibold text-neutral-500">{String(d.scheduled_at).slice(0, 10)}</span>
                <Pill tone="grey">{(d.platforms || [])[0]}</Pill>
                {d.format && <Pill tone="grey">{FORMAT_LABELS[d.format] || d.format}</Pill>}
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-[#111111]">{d.title}</p>
                  <p className="truncate text-xs text-neutral-500">{d.why || d.content}</p>
                </div>
                <Pill tone={d.approval_status === "approved" ? "green" : d.approval_status === "rejected" ? "grey" : "amber"}>
                  {d.approval_status === "approved" ? "Approved" : d.approval_status === "rejected" ? "Rejected" : "Draft"}
                </Pill>
                {d.publish_status === "published" && (
                  <Pill tone="green" data-testid={`content-draft-published-${d.id}`}>Published to Facebook</Pill>
                )}
                {d.publish_status === "failed" && (
                  <Pill tone="red" data-testid={`content-draft-publish-failed-${d.id}`}>Publish failed — try again</Pill>
                )}
                {d.approval_status === "approved" && (d.platforms || [])[0] === "Facebook" && d.publish_status !== "published" && (
                  <button className="inline-flex items-center gap-1 text-xs font-semibold text-neutral-500 hover:text-[#111111]"
                    title="Publish this post to your Facebook Page now" data-testid={`content-draft-publish-${d.id}`}
                    onClick={() => setPublishing(d)}>
                    <Send size={13} /> Publish now
                  </button>
                )}
                <button className="text-neutral-400 hover:text-[#111111]" title="Open creative brief" data-testid={`content-draft-edit-${d.id}`} onClick={() => setEditing(d)}>
                  <Pencil size={14} />
                </button>
                {d.approval_status !== "approved" && (
                  <button className="text-neutral-400 hover:text-green-700" title="Approve" data-testid={`content-draft-approve-${d.id}`} onClick={() => review([d.id], "approve")}>
                    <Check size={15} />
                  </button>
                )}
                {d.approval_status !== "rejected" && (
                  <button className="text-neutral-400 hover:text-red-600" title="Reject" data-testid={`content-draft-reject-${d.id}`} onClick={() => review([d.id], "reject")}>
                    <X size={15} />
                  </button>
                )}
              </div>
            ))}
          </div>
          <p className="mt-4 text-[11px] text-neutral-400">
            Approved posts appear on the Content Calendar tab on their planned date. Nothing is published automatically — an approved Facebook post goes out only when you click Publish now.
          </p>
        </Card>
      )}

      {plans.length > 0 && (
        <div>
          <button className="inline-flex items-center gap-1 text-xs font-semibold text-neutral-500 hover:text-[#111111]" data-testid="content-plan-history-toggle" onClick={() => setShowPlans(!showPlans)}>
            <History size={12} /> Your plans ({plans.length})
          </button>
          {showPlans && (
            <div className="mt-2 flex flex-wrap gap-2" data-testid="content-plan-list">
              {plans.map((p) => (
                <button key={p.id} onClick={() => openPlan(p.id)} data-testid={`content-plan-open-${p.id}`}
                  className={`rounded-full border px-3 py-1 text-xs font-medium ${current?.plan.id === p.id ? "border-[#111111] bg-[#111111] text-white" : "border-neutral-300 text-neutral-600"}`}>
                  {p.start_date} ({p.post_count} posts)
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {editing && (
        <EditDraftModal draft={editing} onClose={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await openPlan(current.plan.id); }} />
      )}

      {publishing && (
        <Modal title="Publish to Facebook" onClose={() => setPublishing(null)} testid="publish-confirm-modal">
          {fbConn?.can_publish ? (
            <div className="space-y-3">
              <p className="text-sm text-neutral-600" data-testid="publish-confirm-page">
                This publishes the post below to your Facebook Page <span className="font-semibold">{fbConn.display_name}</span> immediately. It cannot be unpublished from here.
              </p>
              <div className="rounded-xl border border-neutral-200 bg-neutral-50 p-3" data-testid="publish-confirm-preview">
                <p className="text-xs font-bold uppercase tracking-wider text-neutral-500">{publishing.title}</p>
                <p className="mt-1 whitespace-pre-wrap text-sm text-neutral-700">{publishing.content}</p>
                {(publishing.hashtags || []).length > 0 && (
                  <p className="mt-1 text-xs text-neutral-500">{(publishing.hashtags || []).map((h) => `#${h}`).join(" ")}</p>
                )}
              </div>
              <div className="flex justify-end gap-2">
                <Btn variant="ghost" onClick={() => setPublishing(null)}>Cancel</Btn>
                <Btn data-testid="publish-confirm-button" disabled={publishBusy} onClick={publish}>
                  {publishBusy ? "Publishing…" : "Yes, publish now"}
                </Btn>
              </div>
            </div>
          ) : (
            <div data-testid="publish-blocked-notice">
              <p className="text-sm text-neutral-600">
                {!fbConn
                  ? "No Facebook Page is connected yet. Connect your Page on the Social Accounts tab first — nothing will be published until then."
                  : "Your Facebook connection doesn't have publishing access yet. Reconnect on the Social Accounts tab to grant it — nothing will be published until you do."}
              </p>
              <div className="mt-4 flex justify-end">
                <Btn variant="ghost" data-testid="publish-blocked-close" onClick={() => setPublishing(null)}>Close</Btn>
              </div>
            </div>
          )}
        </Modal>
      )}
    </div>
  );
}
